import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import PermissionsTab from '../components/admin/PermissionsTab';
import CreateStartupModal from '../components/CreateStartupModal';
import FilePreviewModal from '../components/FilePreviewModal';
import FileUpload from '../components/FileUpload';
import CustomDialog from '../components/CustomDialog';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailViewNavigation from '../components/DetailViewNavigation';
import { adminAPI, investorAPI, investmentRoundAPI, fileAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api.config';

const STAGES = ['Idea', 'MVP', 'Pre-Seed', 'Seed', 'Series A', 'Series B', 'Growth', 'Exit'];

// Helper functions for document type/category
const getCategoryLabel = (category) => {
    if (!category) return 'General';
    const categoryMap = {
        'PITCH_DECK': 'Pitch Deck',
        'INVESTOR_PRESENTATION': 'Investor Presentation',
        'EXECUTIVE_SUMMARY': 'Executive Summary',
        'FINANCIAL': 'Financial Statements',
        'AUDITED_FINANCIALS': 'Audited Financials',
        'PROJECTED_FINANCIALS': 'Projected Financials',
        'BALANCE_SHEET': 'Balance Sheet',
        'P_L_STATEMENT': 'P&L Statement',
        'CASH_FLOW_STATEMENT': 'Cash Flow Statement',
        'VALUATION_CERTIFICATE': 'Valuation Certificate',
        'CAP_TABLE': 'Cap Table',
        'LEGAL': 'Legal Documents',
        'INCORPORATION': 'Incorporation Documents',
        'AGREEMENT': 'Agreements',
        'SHA': 'Shareholder Agreement',
        'TERM_SHEET': 'Term Sheet',
        'CCD': 'CCD',
        'SAFE_AGREEMENT': 'SAFE Agreement',
        'CONVERTIBLE_NOTE': 'Convertible Note',
        'EMPLOYMENT_AGREEMENT': 'Employment Agreement',
        'NDA': 'NDA',
        'IP_DOCUMENTS': 'IP Documents',
        'PATENTS': 'Patents',
        'TRADEMARKS': 'Trademarks',
        'BUSINESS_PLAN': 'Business Plan',
        'MARKET_RESEARCH': 'Market Research',
        'COMPETITIVE_ANALYSIS': 'Competitive Analysis',
        'BUSINESS_MODEL': 'Business Model',
        'GO_TO_MARKET': 'Go-to-Market',
        'PRODUCT_DOCUMENTATION': 'Product Docs',
        'TECHNICAL_SPECS': 'Technical Specs',
        'COMPLIANCE': 'Compliance',
        'LICENSES': 'Licenses',
        'REGULATORY_FILINGS': 'Regulatory Filings',
        'TAX_DOCUMENTS': 'Tax Documents',
        'OPERATIONAL': 'Operational',
        'HR_DOCUMENTS': 'HR Documents',
        'POLICIES': 'Policies',
        'PROCESSES': 'Processes',
        'MARKETING_MATERIALS': 'Marketing',
        'SALES_DECK': 'Sales Deck',
        'BRAND_GUIDELINES': 'Brand Guidelines',
        'GENERAL': 'General',
        'OTHER': 'Other'
    };
    return categoryMap[category] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getCategoryColor = (category) => {
    if (!category) return 'bg-gray-100 text-gray-800';
    if (category.includes('PITCH') || category.includes('PRESENTATION') || category.includes('DECK')) {
        return 'bg-purple-100 text-purple-800';
    } else if (category.includes('FINANCIAL') || category.includes('VALUATION') || category.includes('CAP_TABLE') || category.includes('BALANCE') || category.includes('CASH_FLOW') || category.includes('P_L')) {
        return 'bg-green-100 text-green-800';
    } else if (category.includes('LEGAL') || category.includes('AGREEMENT') || category.includes('SHA') || category.includes('TERM') || category.includes('NDA') || category.includes('PATENT') || category.includes('TRADEMARK') || category.includes('IP')) {
        return 'bg-blue-100 text-blue-800';
    } else if (category.includes('BUSINESS') || category.includes('MARKET') || category.includes('COMPETITIVE') || category.includes('PRODUCT') || category.includes('TECHNICAL')) {
        return 'bg-orange-100 text-orange-800';
    } else if (category.includes('COMPLIANCE') || category.includes('LICENSE') || category.includes('REGULATORY') || category.includes('TAX')) {
        return 'bg-yellow-100 text-yellow-800';
    } else if (category.includes('OPERATIONAL') || category.includes('HR') || category.includes('POLICY') || category.includes('PROCESS')) {
        return 'bg-indigo-100 text-indigo-800';
    } else if (category.includes('MARKETING') || category.includes('SALES') || category.includes('BRAND')) {
        return 'bg-pink-100 text-pink-800';
    } else {
        return 'bg-gray-100 text-gray-800';
    }
};

const StartupStageTimeline = ({ currentStage, onStageSelect }) => {
    const currentIndex = STAGES.findIndex(s => (s || '').toLowerCase() === (currentStage || '').toLowerCase());
    const activeIndex = currentIndex === -1 ? 0 : currentIndex;

    return (
        <div className="w-full py-2 px-2 overflow-x-auto bg-white border-b border-gray-100">
            <div className="relative flex items-center justify-between min-w-[500px] h-12">
                {/* Connecting Lines */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-[2px] bg-gray-100 z-0 rounded-full" />
                <div
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-blue-500 z-0 rounded-full transition-all duration-500"
                    style={{ width: `${(activeIndex / (STAGES.length - 1)) * 100}%` }}
                />

                {STAGES.map((stage, index) => {
                    const isActive = index === activeIndex;
                    const isPassed = index < activeIndex;

                    return (
                        <div
                            key={stage}
                            className="relative flex flex-col items-center group z-10 cursor-pointer"
                            onClick={() => onStageSelect && onStageSelect(stage)}
                        >
                            {isActive ? (
                                <div className="relative">
                                    <div className="bg-blue-500 text-white px-2 py-0.5 rounded-full flex items-center space-x-1 shadow-sm border border-white box-content transform transition-transform duration-200 hover:scale-105">
                                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="font-semibold text-[10px] whitespace-nowrap leading-none pb-0.5">{stage}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className={`w-2 h-2 rounded-full border box-content transition-all duration-300 ${isPassed ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-blue-400'
                                    }`}>
                                    {/* Hover tooltip for non-active stages */}
                                    <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-1.5 py-0.5 bg-gray-800 text-white text-[9px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                        {stage}
                                    </div>
                                </div>
                            )}

                            {/* Decision Icons for final stage - scaled down significantly */}
                            {index === STAGES.length - 1 && (
                                <div className="absolute top-4 flex space-x-1 opacity-20 hover:opacity-100 transition-opacity">
                                    <div className="bg-green-50 rounded-full border border-green-200">
                                        <svg className="w-2 h-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" /></svg>
                                    </div>
                                    <div className="bg-red-50 rounded-full border border-red-200">
                                        <svg className="w-2 h-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" /></svg>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const StartupDetail = () => {
    const { tenantId, id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [startup, setStartup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
    const [files, setFiles] = useState([]);
    const [directors, setDirectors] = useState([]);
    const [stageHistory, setStageHistory] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddDirectorModal, setShowAddDirectorModal] = useState(false);
    const [showEditDirectorModal, setShowEditDirectorModal] = useState(false);
    const [editingDirector, setEditingDirector] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [selectedFileForPermissions, setSelectedFileForPermissions] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [investors, setInvestors] = useState([]);
    const [investments, setInvestments] = useState([]);
    const [newDirector, setNewDirector] = useState({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showDeleteDirectorConfirm, setShowDeleteDirectorConfirm] = useState(false);
    const [directorToDelete, setDirectorToDelete] = useState(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState('ALL');
    const [editData, setEditData] = useState({
        companyName: '',
        industry: '',
        stage: '',
        description: '',
        website: '',
        phoneNumber: ''
    });

    // Investment Rounds state
    const [investmentSubTab, setInvestmentSubTab] = useState('investments');
    const [rounds, setRounds] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [allInvestors, setAllInvestors] = useState([]);
    const [selectedRound, setSelectedRound] = useState(null);
    const [selectedCommitment, setSelectedCommitment] = useState(null);
    const [showRoundModal, setShowRoundModal] = useState(false);
    const [showCommitmentModal, setShowCommitmentModal] = useState(false);
    const [showParticipationModal, setShowParticipationModal] = useState(false);
    const [showTermModal, setShowTermModal] = useState(false);
    const [editingRound, setEditingRound] = useState(null);
    const [roundForm, setRoundForm] = useState({
        roundName: 'Seed',
        targetAmount: '',
        sharePrice: '',
        preMoneyValuation: '',
        postMoneyValuation: '',
        totalShares: '',
        openDate: new Date().toISOString().split('T')[0],
        closeDate: '',
        notes: ''
    });
    const [commitmentForm, setCommitmentForm] = useState({
        networkId: '',
        committedAmount: '',
        equityPercentage: '',
        numberOfShares: '',
        investmentType: 'EQUITY',
        notes: '',
        networkInvestmentAmount: '',
        selectedMemberIds: []
    });
    const [networkMembers, setNetworkMembers] = useState([]);
    const [participationForm, setParticipationForm] = useState({
        investorId: '',
        amount: '',
        participationPercentage: '',
        sharesAllocated: '',
        notes: ''
    });
    const [termForm, setTermForm] = useState({
        amount: '',
        percentage: '',
        dueDate: '',
        milestone: '',
        notes: ''
    });
    const [confirm, setConfirm] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'danger',
        onConfirm: null
    });

    // Sync query param to activeTab when URL changes
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && ['overview', 'files', 'investments', 'stage-history', 'founder', 'directors'].includes(tabFromUrl)) {
            // Support both 'founder' and 'directors' for backward compatibility - map 'founder' to 'directors'
            setActiveTab(tabFromUrl === 'founder' ? 'directors' : tabFromUrl);
        }
    }, [searchParams]);

    // Update URL when tab changes
    useEffect(() => {
        setSearchParams({ tab: activeTab });
    }, [activeTab, setSearchParams]);

    useEffect(() => {
        // Load all data in parallel for faster page load
        Promise.all([
            loadStartupDetails(),
            loadFiles(),
            loadDirectors(),
            loadStageHistory(),
            loadInvestors(),
            loadInvestments(),
            loadInvestmentRounds(),
            loadNetworksAndInvestors()
        ]);
    }, [id]);

    const loadStartupDetails = async () => {
        setLoading(true);
        try {
            let data;
            if (user?.role === 'INVESTOR') {
                data = await investorAPI.getStartupProfile(id);
            } else {
                const response = await fetch(getApiUrl(`admin/users/${id}`), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                data = await response.json();
            }
            setStartup(data);
        } catch (error) {
            console.error('Failed to load startup details:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadFiles = async () => {
        try {
            const response = await fetch(getApiUrl(`files?moduleType=STARTUP&moduleId=${id}`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFiles(data);
            } else if (response.status === 204) {
                // No content - no files found
                setFiles([]);
            }
        } catch (error) {
            console.error('Failed to load files:', error);
            setFiles([]);
        }
    };

    const loadDirectors = async () => {
        try {
            // Use the user ID to fetch directors (since directors are linked to startups via user email/company name)
            let data;
            if (user?.role === 'INVESTOR') {
                data = await investorAPI.getStartupDirectors(id);
            } else {
                data = await adminAPI.getDirectorsByUserId(id);
            }
            setDirectors(data || []);
        } catch (error) {
            console.error('Failed to load directors:', error);
            setDirectors([]);
        }
    };

    const loadInvestors = async () => {
        if (user?.role === 'INVESTOR') return;
        try {
            const data = await adminAPI.getInvestors();
            setInvestors(data);
        } catch (error) {
            console.error('Failed to load investors:', error);
        }
    };

    const loadInvestments = async () => {
        try {
            // Use investorAPI for investors, or direct fetch for others (or add to adminAPI)
            // Since the endpoint is shared and logic is in backend, we can use the same endpoint
            let data;
            if (user?.role === 'INVESTOR') {
                data = await investorAPI.getStartupInvestments(id);
            } else {
                // Reuse the same endpoint structure, it works for Admin/Startup too
                const response = await fetch(getApiUrl(`investments/startup/${id}`), {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                data = await response.json();
            }
            setInvestments(data || []);
        } catch (error) {
            console.error('Failed to load investments:', error);
            setInvestments([]);
        }
    };

    // Investment Rounds functions
    const loadInvestmentRounds = async () => {
        try {
            const roundsData = await investmentRoundAPI.getRoundsByStartup(id);
            setRounds(roundsData || []);
        } catch (error) {
            console.error('Failed to load investment rounds:', error);
            setRounds([]);
        }
    };

    const loadNetworksAndInvestors = async () => {
        try {
            const usersData = await adminAPI.getAllUsers();
            setNetworks(usersData.filter(u => u.role === 'INVESTOR_NETWORK'));
            setAllInvestors(usersData.filter(u => u.role === 'INVESTOR'));
        } catch (error) {
            console.error('Failed to load networks and investors:', error);
        }
    };

    const handleCreateRound = async () => {
        try {
            await investmentRoundAPI.createRound({
                ...roundForm,
                startupId: id,
                targetAmount: parseFloat(roundForm.targetAmount) || null,
                sharePrice: parseFloat(roundForm.sharePrice) || null,
                preMoneyValuation: parseFloat(roundForm.preMoneyValuation) || null,
                postMoneyValuation: parseFloat(roundForm.postMoneyValuation) || null,
                totalShares: parseInt(roundForm.totalShares) || null
            });
            setSuccessMessage('Investment round created successfully');
            setShowSuccessDialog(true);
            setShowRoundModal(false);
            resetRoundForm();
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to create round');
            setShowErrorDialog(true);
        }
    };

    const handleUpdateRound = async () => {
        try {
            await investmentRoundAPI.updateRound(editingRound.id, {
                ...roundForm,
                targetAmount: parseFloat(roundForm.targetAmount) || null,
                sharePrice: parseFloat(roundForm.sharePrice) || null,
                preMoneyValuation: parseFloat(roundForm.preMoneyValuation) || null,
                postMoneyValuation: parseFloat(roundForm.postMoneyValuation) || null,
                totalShares: parseInt(roundForm.totalShares) || null
            });
            setSuccessMessage('Round updated successfully');
            setShowSuccessDialog(true);
            setShowRoundModal(false);
            setEditingRound(null);
            resetRoundForm();
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to update round');
            setShowErrorDialog(true);
        }
    };

    const handleCloseRound = async (round) => {
        if (!window.confirm(`Are you sure you want to close the "${round.roundName}" round? This action cannot be undone.`)) return;
        try {
            await investmentRoundAPI.closeRound(round.id);
            setSuccessMessage('Round closed successfully');
            setShowSuccessDialog(true);
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to close round');
            setShowErrorDialog(true);
        }
    };

    const handleDeleteRound = async (round) => {
        if (!window.confirm(`Are you sure you want to delete the "${round.roundName}" round?`)) return;
        try {
            await investmentRoundAPI.deleteRound(round.id);
            setSuccessMessage('Round deleted successfully');
            setShowSuccessDialog(true);
            setSelectedRound(null);
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to delete round');
            setShowErrorDialog(true);
        }
    };

    const handleCreateCommitment = async () => {
        try {
            await investmentRoundAPI.createCommitment(selectedRound.id, {
                networkId: commitmentForm.networkId,
                committedAmount: parseFloat(commitmentForm.committedAmount),
                equityPercentage: parseFloat(commitmentForm.equityPercentage) || null,
                numberOfShares: parseInt(commitmentForm.numberOfShares) || null,
                investmentType: commitmentForm.investmentType,
                notes: commitmentForm.notes,
                networkInvestmentAmount: parseFloat(commitmentForm.networkInvestmentAmount) || null,
                selectedMemberIds: commitmentForm.selectedMemberIds
            });
            setSuccessMessage('Commitment created successfully');
            setShowSuccessDialog(true);
            setShowCommitmentModal(false);
            resetCommitmentForm();
            const updatedRound = await investmentRoundAPI.getRound(selectedRound.id);
            setSelectedRound(updatedRound);
            loadInvestmentRounds();
        } catch (error) {
            setErrorMessage(error.message || 'Failed to create commitment');
            setShowErrorDialog(true);
        }
    };

    const handleCreateParticipation = async () => {
        try {
            await investmentRoundAPI.createParticipation(selectedCommitment.id, {
                ...participationForm,
                amount: parseFloat(participationForm.amount),
                participationPercentage: parseFloat(participationForm.participationPercentage) || null,
                sharesAllocated: parseInt(participationForm.sharesAllocated) || null
            });
            setSuccessMessage('Participation added successfully');
            setShowSuccessDialog(true);
            setShowParticipationModal(false);
            resetParticipationForm();
            const updatedCommitment = await investmentRoundAPI.getCommitment(selectedCommitment.id);
            setSelectedCommitment(updatedCommitment);
        } catch (error) {
            setErrorMessage(error.message || 'Failed to add participation');
            setShowErrorDialog(true);
        }
    };

    const handleCreateTerm = async () => {
        try {
            await investmentRoundAPI.createTerm(selectedCommitment.id, {
                ...termForm,
                amount: parseFloat(termForm.amount),
                percentage: parseFloat(termForm.percentage) || null
            });
            setSuccessMessage('Term created successfully');
            setShowSuccessDialog(true);
            setShowTermModal(false);
            resetTermForm();
            const updatedCommitment = await investmentRoundAPI.getCommitment(selectedCommitment.id);
            setSelectedCommitment(updatedCommitment);
        } catch (error) {
            setErrorMessage(error.message || 'Failed to create term');
            setShowErrorDialog(true);
        }
    };

    const handleMarkTermPaid = async (term) => {
        const reference = prompt('Enter transaction reference (optional):');
        if (reference === null) return; // User cancelled
        try {
            await investmentRoundAPI.markTermAsPaid(term.id, reference);
            setSuccessMessage('Term marked as paid');
            setShowSuccessDialog(true);
            const updatedCommitment = await investmentRoundAPI.getCommitment(selectedCommitment.id);
            setSelectedCommitment(updatedCommitment);
        } catch (error) {
            setErrorMessage(error.message || 'Failed to mark term as paid');
            setShowErrorDialog(true);
        }
    };

    const resetRoundForm = () => {
        setRoundForm({
            roundName: 'Seed',
            targetAmount: '',
            sharePrice: '',
            preMoneyValuation: '',
            postMoneyValuation: '',
            totalShares: '',
            openDate: new Date().toISOString().split('T')[0],
            closeDate: '',
            notes: ''
        });
    };

    const resetCommitmentForm = () => {
        setCommitmentForm({
            networkId: '',
            committedAmount: '',
            equityPercentage: '',
            numberOfShares: '',
            investmentType: 'EQUITY',
            notes: '',
            networkInvestmentAmount: '',
            selectedMemberIds: []
        });
        setNetworkMembers([]);
    };

    const loadNetworkMembers = async (networkId) => {
        if (!networkId) {
            setNetworkMembers([]);
            return;
        }
        try {
            const response = await fetch(getApiUrl(`investor-network/${networkId}/investors`), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const members = await response.json();
                setNetworkMembers(members || []);
            }
        } catch (error) {
            console.error('Failed to load network members:', error);
            setNetworkMembers([]);
        }
    };

    const resetParticipationForm = () => {
        setParticipationForm({
            investorId: '',
            amount: '',
            participationPercentage: '',
            sharesAllocated: '',
            notes: ''
        });
    };

    const resetTermForm = () => {
        setTermForm({
            amount: '',
            percentage: '',
            dueDate: '',
            milestone: '',
            notes: ''
        });
    };

    const openEditRound = (round) => {
        setEditingRound(round);
        setRoundForm({
            roundName: round.roundName,
            targetAmount: round.targetAmount || '',
            sharePrice: round.sharePrice || '',
            preMoneyValuation: round.preMoneyValuation || '',
            postMoneyValuation: round.postMoneyValuation || '',
            totalShares: round.totalShares || '',
            openDate: round.openDate || '',
            closeDate: round.closeDate || '',
            notes: round.notes || ''
        });
        setShowRoundModal(true);
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getRoundStatusBadge = (status) => {
        const colors = {
            OPEN: 'bg-green-100 text-green-800',
            CLOSED: 'bg-gray-100 text-gray-800',
            CANCELLED: 'bg-red-100 text-red-800',
            COMMITTED: 'bg-blue-100 text-blue-800',
            PARTIAL: 'bg-yellow-100 text-yellow-800',
            COMPLETED: 'bg-green-100 text-green-800',
            PENDING: 'bg-yellow-100 text-yellow-800',
            PAID: 'bg-green-100 text-green-800',
            OVERDUE: 'bg-red-100 text-red-800',
            ACTIVE: 'bg-green-100 text-green-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const loadStageHistory = async () => {
        try {
            // TODO: Implement stage history loading
            setStageHistory([
                { stage: 'Idea', date: '2023-01-15', amount: null },
                { stage: 'Seed', date: '2023-06-20', amount: '$500K' },
                { stage: 'Series A', date: '2024-03-10', amount: '$2M' }
            ]);
        } catch (error) {
            console.error('Failed to load stage history:', error);
        }
    };

    const openEditModal = () => {
        setEditData({
            companyName: startup.organizationName || '',
            industry: startup.department || '',
            stage: startup.jobTitle || '',
            description: startup.description || '',
            website: startup.website || '',
            phoneNumber: startup.phoneNumber || ''
        });
        setShowEditModal(true);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(editData)
            });

            if (response.ok) {
                setShowEditModal(false);
                loadStartupDetails();
                setAlert({ isOpen: true, title: 'Success', message: 'Startup updated successfully!', type: 'success' });
            } else {
                setAlert({ isOpen: true, title: 'Error', message: 'Failed to update startup', type: 'error' });
            }
        } catch (error) {
            console.error('Failed to update startup:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to update startup', type: 'error' });
        }
    };

    const handleStageSelect = async (newStage) => {
        const previousStage = startup.jobTitle;
        // Optimistic update
        setStartup(prev => ({ ...prev, jobTitle: newStage }));

        try {
            const payload = {
                companyName: startup.organizationName,
                industry: startup.department,
                stage: newStage,
                description: startup.description,
                website: startup.website,
                phoneNumber: startup.phoneNumber
            };

            const response = await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMsg = errorData.message || errorData.error || 'Failed to update stage';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Failed to update stage:', error);
            // Revert on failure
            setStartup(prev => ({ ...prev, jobTitle: previousStage }));
            setErrorMessage(error.message || 'Failed to update stage. Please try again.');
            setShowErrorDialog(true);
        }
    };

    const handleDeleteStartup = async () => {
        try {
            await fetch(getApiUrl(`admin/users/${id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            navigate(`/${tenantId}/startups`);
        } catch (error) {
            console.error('Failed to delete startup:', error);
        }
    };

    // handleFileUpload function removed as it is replaced by FileUpload component

    const handleAddDirector = async () => {
        if (!newDirector.panNumber || !newDirector.aadharNumber) {
            setErrorMessage('PAN Number and Aadhaar Number are mandatory.');
            setShowErrorDialog(true);
            return;
        }

        try {
            const directorData = {
                name: newDirector.name,
                email: newDirector.email || null,
                phone: newDirector.phone || null,
                role: newDirector.role || null,
                category: newDirector.category || null,
                shareholding: newDirector.shareholding || null,
                aadharNumber: newDirector.aadharNumber,
                panNumber: newDirector.panNumber
            };
            const createdDirector = await adminAPI.createDirectorByUserId(id, directorData);
            setDirectors([...directors, createdDirector]);
            setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
            setShowAddDirectorModal(false);
            setSuccessMessage('Director added successfully!');
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Failed to add director:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to add director');
            setShowErrorDialog(true);
        }
    };

    const handleEditDirector = (director) => {
        setEditingDirector(director);
        setNewDirector({
            name: director.name || '',
            email: director.email || '',
            phone: director.phone || '',
            role: director.role || '',
            category: director.category || '',
            shareholding: director.shareholding || '',
            aadharNumber: director.aadharNumber || '',
            panNumber: director.panNumber || ''
        });
        setShowEditDirectorModal(true);
    };

    const handleUpdateDirector = async () => {
        if (!newDirector.panNumber || !newDirector.aadharNumber) {
            setErrorMessage('PAN Number and Aadhaar Number are mandatory.');
            setShowErrorDialog(true);
            return;
        }

        try {
            const directorData = {
                name: newDirector.name,
                email: newDirector.email || null,
                phone: newDirector.phone || null,
                role: newDirector.role || null,
                category: newDirector.category || null,
                shareholding: newDirector.shareholding || null,
                aadharNumber: newDirector.aadharNumber,
                panNumber: newDirector.panNumber
            };
            const updatedDirector = await adminAPI.updateDirector(editingDirector.id, directorData);
            setDirectors(directors.map(d => d.id === editingDirector.id ? updatedDirector : d));
            setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
            setEditingDirector(null);
            setShowEditDirectorModal(false);
            setSuccessMessage('Director updated successfully!');
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Failed to update director:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to update director');
            setShowErrorDialog(true);
        }
    };

    const handleDeleteDirector = async (directorId) => {
        try {
            await adminAPI.deleteDirector(directorId);
            setDirectors(directors.filter(d => d.id !== directorId));
            setSuccessMessage('Director deleted successfully!');
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Failed to delete director:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to delete director');
            setShowErrorDialog(true);
        }
    };

    const handleDeleteFile = (fileId, fileName) => {
        setConfirm({
            isOpen: true,
            title: 'Delete File',
            message: `Are you sure you want to delete "${fileName}"?`,
            type: 'danger',
            onConfirm: async () => {
                try {
                    await fileAPI.deleteFile(fileId);
                    loadFiles();
                    setSuccessMessage('File deleted successfully');
                    setShowSuccessDialog(true);
                } catch (err) {
                    console.error('Failed to delete file:', err);
                    setErrorMessage('Failed to delete file');
                    setShowErrorDialog(true);
                }
            }
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!startup) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900">Startup not found</h2>
                    <button onClick={() => navigate(user?.role === 'INVESTOR' ? `/${tenantId}/investor/dashboard` : `/${tenantId}/startups`)} className="mt-4 text-blue-600 hover:text-blue-800">
                        ← Back to {user?.role === 'INVESTOR' ? 'Dashboard' : 'Startups'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Page Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4">
                            <button onClick={() => navigate(user?.role === 'INVESTOR' ? `/${tenantId}/investor/dashboard` : `/${tenantId}/startups`)} className="text-gray-600 hover:text-gray-900">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="flex items-center space-x-4">
                                <h1 className="text-2xl font-bold text-gray-900">{startup.organizationName || 'Startup Name'}</h1>
                                <div className="h-6 w-px bg-gray-300"></div>
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-gray-500">Founder:</span>
                                    <span className="text-sm font-semibold text-gray-900">{startup.name}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            {/* Navigation */}
                            <DetailViewNavigation type="startup" currentId={id} />

                            {/* Action Buttons */}
                            {/* Action Buttons - Only for Admin */}
                            {user?.role !== 'INVESTOR' && (
                                <div className="flex space-x-3">
                                    <button onClick={openEditModal} className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50 transition" title="Edit Startup">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                    <button onClick={() => setShowDeleteModal(true)} className="p-2 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition" title="Delete Startup">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stage Timeline - Full Width Row */}
                    <div className="w-full">
                        <StartupStageTimeline
                            currentStage={startup.jobTitle}
                            onStageSelect={user?.role === 'INVESTOR' ? undefined : handleStageSelect}
                        />
                    </div>

                    {/* Tabs */}
                    <div className="mt-4 border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            {[
                                { id: 'overview', label: 'Overview' },
                                { id: 'files', label: 'Files' },
                                { id: 'investments', label: 'Investments' },
                                { id: 'stage-history', label: 'Stage History' },
                                { id: 'directors', label: 'Directors' }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        setSearchParams({ tab: tab.id });
                                    }}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                {/* Overview Tab */}
                {activeTab === 'overview' && startup && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                            {/* Quick Stats */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-sm font-medium text-gray-500 mb-4">Quick Stats</h3>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-2xl font-bold text-gray-900">{files.length || 0}</p>
                                        <p className="text-sm text-gray-500">Documents</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-900">{directors.length || 0}</p>
                                        <p className="text-sm text-gray-500">Directors</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-900">{stageHistory.length || 0}</p>
                                        <p className="text-sm text-gray-500">Funding Rounds</p>
                                    </div>
                                </div>
                            </div>

                            {/* Company Info */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                                <dl className="space-y-3">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.organizationName || startup.name || 'N/A'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Industry</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.department || startup.industry || 'N/A'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Current Stage</dt>
                                        <dd className="mt-1">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                                {startup.jobTitle || startup.stage || 'Not specified'}
                                            </span>
                                        </dd>
                                    </div>
                                    {(startup.website || startup.organizationType) && (
                                        <>
                                            {startup.website && (
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Website</dt>
                                                    <dd className="mt-1 text-sm text-gray-900">
                                                        <a href={startup.website.startsWith('http') ? startup.website : `https://${startup.website}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-800">
                                                            {startup.website}
                                                        </a>
                                                    </dd>
                                                </div>
                                            )}
                                            {startup.organizationType && (
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Category</dt>
                                                    <dd className="mt-1 text-sm text-gray-900">{startup.organizationType}</dd>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </dl>
                            </div>

                            {/* Contact Info */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                                <dl className="space-y-3">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Primary Contact</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.name || 'N/A'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Email</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.email || 'N/A'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500">Phone</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{startup.phoneNumber || 'N/A'}</dd>
                                    </div>
                                </dl>
                            </div>

                            {/* Company Location */}
                            {(startup.officialAddress || startup.branchAddress || startup.city || startup.state || startup.country || startup.address) && (
                                <div className="bg-white rounded-lg shadow p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Location</h3>
                                    <dl className="space-y-3">
                                        {startup.officialAddress && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Official Address</dt>
                                                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{startup.officialAddress}</dd>
                                            </div>
                                        )}
                                        {startup.branchAddress && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Branch Address</dt>
                                                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{startup.branchAddress}</dd>
                                            </div>
                                        )}
                                        {startup.address && !startup.officialAddress && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Address</dt>
                                                <dd className="mt-1 text-sm text-gray-900">{startup.address}</dd>
                                            </div>
                                        )}
                                        {(startup.city || startup.state || startup.country) && (
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500">Location</dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    {[startup.city, startup.state, startup.country].filter(Boolean).join(', ') || 'N/A'}
                                                </dd>
                                            </div>
                                        )}
                                    </dl>
                                </div>
                            )}
                        </div>

                        {/* Recent Activity */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                            <div className="text-center py-8 text-gray-500">
                                <p>No recent activity</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Files Tab */}
                {activeTab === 'files' && (() => {
                    // Get all unique document types from files
                    const getAllDocumentTypes = () => {
                        const types = new Set();
                        files.forEach(file => {
                            if (file.documentType || file.fileCategory) {
                                types.add(file.documentType || file.fileCategory);
                            }
                        });
                        return Array.from(types).sort();
                    };

                    // Filter files based on selected document type
                    const filteredFiles = files.filter(file => {
                        const docType = file.documentType || file.fileCategory;
                        return selectedDocumentType === 'ALL' || docType === selectedDocumentType;
                    });

                    return (
                        <div className="space-y-4">
                            <div className="bg-white rounded-lg shadow">
                                {/* Header */}
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-lg font-semibold text-gray-900">Documents & Files</h2>
                                        <div className="flex items-center space-x-3">
                                            <label className="text-sm font-medium text-gray-700">Filter by Type:</label>
                                            <select
                                                value={selectedDocumentType}
                                                onChange={(e) => setSelectedDocumentType(e.target.value)}
                                                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            >
                                                <option value="ALL">All Types</option>
                                                {getAllDocumentTypes().map(type => (
                                                    <option key={type} value={type}>
                                                        {getCategoryLabel(type)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Compact File Upload - Hide for Investor */}
                                {user?.role !== 'INVESTOR' && (
                                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                        <FileUpload
                                            moduleType="STARTUP"
                                            moduleId={id}
                                            onUploadSuccess={() => {
                                                loadFiles();
                                                setSuccessMessage('File uploaded successfully!');
                                                setShowSuccessDialog(true);
                                            }}
                                            onUploadError={(msg) => {
                                                setErrorMessage(msg || 'Failed to upload file');
                                                setShowErrorDialog(true);
                                            }}
                                        />
                                    </div>
                                )}

                                {/* File List */}
                                {filteredFiles.length === 0 ? (
                                    <div className="px-6 py-12 text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                                            {files.length === 0 ? 'No files' : 'No files match the selected filter'}
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {files.length === 0
                                                ? 'Upload pitch decks, financial statements, business plans, etc.'
                                                : 'Try selecting a different document type filter.'}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-200">
                                        {filteredFiles.map((file) => {
                                            const docType = file.documentType || file.fileCategory;
                                            return (
                                                <div
                                                    key={file.id}
                                                    className="px-6 py-3 hover:bg-gray-50 transition cursor-pointer"
                                                    onClick={() => {
                                                        setPreviewFile(file);
                                                        setShowPreviewModal(true);
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        {/* File Info */}
                                                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                            <div className="flex-shrink-0">
                                                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center space-x-2 mb-1">
                                                                    <p className="text-sm font-medium text-gray-900 truncate" title={file.originalName}>
                                                                        {file.originalName}
                                                                    </p>
                                                                    {docType && (
                                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(docType)}`}>
                                                                            {getCategoryLabel(docType)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center space-x-3 mt-1">
                                                                    <p className="text-xs text-gray-500">
                                                                        {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                                                                    </p>
                                                                    {file.uploadedByName && (
                                                                        <>
                                                                            <span className="text-xs text-gray-400">•</span>
                                                                            <p className="text-xs text-gray-500">
                                                                                Uploaded by {file.uploadedByName}
                                                                            </p>
                                                                            {file.uploadedAt && (
                                                                                <>
                                                                                    <span className="text-xs text-gray-400">•</span>
                                                                                    <p className="text-xs text-gray-500">
                                                                                        {new Date(file.uploadedAt).toLocaleDateString()}
                                                                                    </p>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {file.permissions && file.permissions.length > 0 && (
                                                                        <>
                                                                            <span className="text-xs text-gray-400">•</span>
                                                                            <p className="text-xs text-blue-600">
                                                                                {file.permissionCount || file.permissions.length} permission{file.permissionCount !== 1 ? 's' : ''}
                                                                            </p>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="flex items-center space-x-2 ml-4">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setPreviewFile(file);
                                                                    setShowPreviewModal(true);
                                                                }}
                                                                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition inline-flex items-center space-x-1"
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                </svg>
                                                                <span>Preview</span>
                                                            </button>
                                                            {user?.role !== 'INVESTOR' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedFileForPermissions(file);
                                                                        setShowPermissionsModal(true);
                                                                    }}
                                                                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition inline-flex items-center space-x-1"
                                                                >
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                                    </svg>
                                                                    <span>Permissions</span>
                                                                </button>
                                                            )}
                                                            {(user?.id === file.uploadedById ||
                                                                user?.role === 'ADMIN' ||
                                                                (user?.role === 'STARTUP' && user?.id === startup?.id) ||
                                                                (file.permissions && file.permissions.some(p => p.userId === user?.id && p.permissionType === 'DELETE'))) && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteFile(file.id, file.originalName);
                                                                        }}
                                                                        className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition inline-flex items-center space-x-1"
                                                                        title="Delete File"
                                                                    >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Investments Tab */}
                {activeTab === 'investments' && (
                    <div className="space-y-6">
                        {/* Sub-tabs for Investments and Rounds */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="border-b border-gray-200">
                                <nav className="flex space-x-4 px-6" aria-label="Investments Sub-tabs">
                                    <button
                                        onClick={() => setInvestmentSubTab('investments')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm ${investmentSubTab === 'investments'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        Investments
                                    </button>
                                    <button
                                        onClick={() => setInvestmentSubTab('rounds')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm ${investmentSubTab === 'rounds'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        Investment Rounds
                                    </button>
                                </nav>
                            </div>

                            {/* Investments Sub-tab Content */}
                            {investmentSubTab === 'investments' && (
                                <div className="p-6">
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h2 className="text-xl font-semibold text-gray-900">Investments</h2>
                                            <p className="text-sm text-gray-500 mt-1">Track all investments received by this startup</p>
                                        </div>
                                    </div>

                                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investment</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Post Money Holding %</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">No. of CCPS</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Price</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {investments.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                            </svg>
                                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No investments found</h3>
                                                            <p className="mt-1 text-sm text-gray-500">Investments will appear here once they are recorded.</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    investments.map((investment) => (
                                                        <tr key={investment.id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                <div className="flex items-center">
                                                                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold mr-3">
                                                                        {investment.investorName?.charAt(0) || 'I'}
                                                                    </div>
                                                                    <div>
                                                                        <div className="font-medium text-gray-900">{investment.investorName}</div>
                                                                        <div className="text-gray-500 text-xs">{investment.investorEmail}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {investment.investmentAmount ?
                                                                    new Intl.NumberFormat('en-IN', { style: 'currency', currency: investment.currency || 'INR' }).format(investment.investmentAmount)
                                                                    : '-'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {investment.equityPercentage ? `${investment.equityPercentage}%` : '-'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {investment.numberOfShares || '-'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {investment.entrySharePrice ?
                                                                    new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 2 }).format(investment.entrySharePrice)
                                                                    : '-'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${investment.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                    {investment.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Investment Rounds Sub-tab Content */}
                            {investmentSubTab === 'rounds' && (
                                <div className="p-6">
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h2 className="text-xl font-semibold text-gray-900">Investment Rounds</h2>
                                            <p className="text-sm text-gray-500 mt-1">Manage funding rounds, commitments, and terms</p>
                                        </div>
                                        {user?.role !== 'INVESTOR' && (
                                            <button
                                                onClick={() => { resetRoundForm(); setEditingRound(null); setShowRoundModal(true); }}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                                            >
                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                                </svg>
                                                New Round
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                        {/* Rounds List */}
                                        <div className="lg:col-span-1">
                                            <div className="bg-gray-50 rounded-lg border">
                                                <div className="p-4 border-b bg-white rounded-t-lg">
                                                    <h3 className="text-lg font-semibold">Rounds</h3>
                                                </div>
                                                <div className="divide-y max-h-[500px] overflow-y-auto">
                                                    {rounds.length === 0 ? (
                                                        <div className="p-6 text-center text-gray-500">
                                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            <p className="mt-2">No investment rounds</p>
                                                            <p className="text-sm">Click "New Round" to create one</p>
                                                        </div>
                                                    ) : (
                                                        rounds.map(round => (
                                                            <div
                                                                key={round.id}
                                                                onClick={() => { setSelectedRound(round); setSelectedCommitment(null); }}
                                                                className={`p-4 cursor-pointer hover:bg-white ${selectedRound?.id === round.id ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-white'
                                                                    }`}
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <div>
                                                                        <h4 className="font-semibold text-gray-900">{round.roundName}</h4>
                                                                    </div>
                                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoundStatusBadge(round.status)}`}>
                                                                        {round.status}
                                                                    </span>
                                                                </div>
                                                                <div className="mt-2 flex items-center justify-between text-sm">
                                                                    <span className="text-gray-600">
                                                                        {formatCurrency(round.raisedAmount)} / {formatCurrency(round.targetAmount)}
                                                                    </span>
                                                                    <span className="text-gray-500">
                                                                        {round.progressPercentage?.toFixed(0) || 0}%
                                                                    </span>
                                                                </div>
                                                                {round.targetAmount > 0 && (
                                                                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className="bg-blue-600 h-2 rounded-full"
                                                                            style={{ width: `${Math.min(round.progressPercentage || 0, 100)}%` }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Round Details */}
                                        <div className="lg:col-span-2">
                                            {selectedRound ? (
                                                <div className="space-y-6">
                                                    {/* Round Info */}
                                                    <div className="bg-gray-50 rounded-lg border p-6">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <h3 className="text-xl font-bold text-gray-900">{selectedRound.roundName}</h3>
                                                            {user?.role !== 'INVESTOR' && (
                                                                <div className="flex space-x-2">
                                                                    <button
                                                                        onClick={() => openEditRound(selectedRound)}
                                                                        className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                    {selectedRound.status === 'OPEN' && (
                                                                        <button
                                                                            onClick={() => handleCloseRound(selectedRound)}
                                                                            className="px-3 py-1 text-orange-600 hover:bg-orange-50 rounded"
                                                                        >
                                                                            Close
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleDeleteRound(selectedRound)}
                                                                        className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                            <div>
                                                                <p className="text-sm text-gray-500">Target</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.targetAmount)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Raised</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.raisedAmount)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Pre-Money</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.preMoneyValuation)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Post-Money</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.postMoneyValuation)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Share Price</p>
                                                                <p className="font-semibold">{formatCurrency(selectedRound.sharePrice)}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Total Shares</p>
                                                                <p className="font-semibold">{selectedRound.totalShares?.toLocaleString() || '-'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Open Date</p>
                                                                <p className="font-semibold">{selectedRound.openDate || '-'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500">Close Date</p>
                                                                <p className="font-semibold">{selectedRound.closeDate || '-'}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Commitments */}
                                                    <div className="bg-gray-50 rounded-lg border">
                                                        <div className="p-4 border-b bg-white rounded-t-lg flex items-center justify-between">
                                                            <h4 className="text-lg font-semibold">Network Commitments</h4>
                                                            {selectedRound.status === 'OPEN' && user?.role !== 'INVESTOR' && (
                                                                <button
                                                                    onClick={() => { resetCommitmentForm(); setShowCommitmentModal(true); }}
                                                                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                                                >
                                                                    Add Commitment
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="divide-y">
                                                            {selectedRound.commitments?.length === 0 ? (
                                                                <div className="p-6 text-center text-gray-500">
                                                                    No commitments yet
                                                                </div>
                                                            ) : (
                                                                selectedRound.commitments?.map(commitment => (
                                                                    <div
                                                                        key={commitment.id}
                                                                        onClick={() => setSelectedCommitment(commitment)}
                                                                        className={`p-4 cursor-pointer hover:bg-white ${selectedCommitment?.id === commitment.id ? 'bg-blue-50' : 'bg-white'
                                                                            }`}
                                                                    >
                                                                        <div className="flex items-center justify-between">
                                                                            <div>
                                                                                <h5 className="font-semibold">{commitment.networkName}</h5>
                                                                                <p className="text-sm text-gray-500">{commitment.investmentType}</p>
                                                                            </div>
                                                                            <div className="text-right">
                                                                                <p className="font-semibold">{formatCurrency(commitment.committedAmount)}</p>
                                                                                <span className={`px-2 py-0.5 rounded-full text-xs ${getRoundStatusBadge(commitment.status)}`}>
                                                                                    {commitment.status}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                                                                            <span>Disbursed: {formatCurrency(commitment.disbursedAmount)}</span>
                                                                            <span>{commitment.disbursementProgress?.toFixed(0) || 0}%</span>
                                                                        </div>
                                                                        <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                                                                            <div
                                                                                className="bg-green-600 h-1.5 rounded-full"
                                                                                style={{ width: `${Math.min(commitment.disbursementProgress || 0, 100)}%` }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Commitment Details */}
                                                    {selectedCommitment && (
                                                        <div className="bg-gray-50 rounded-lg border">
                                                            <div className="p-4 border-b bg-white rounded-t-lg">
                                                                <h4 className="text-lg font-semibold">
                                                                    Commitment Details - {selectedCommitment.networkName}
                                                                </h4>
                                                            </div>

                                                            {/* Participations */}
                                                            <div className="p-4 border-b bg-white">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h5 className="font-medium text-gray-700">Investor Participations</h5>
                                                                    {user?.role !== 'INVESTOR' && (
                                                                        <button
                                                                            onClick={() => { resetParticipationForm(); setShowParticipationModal(true); }}
                                                                            className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                                                                        >
                                                                            + Add
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {selectedCommitment.participations?.length === 0 ? (
                                                                    <p className="text-gray-500 text-sm">No participations</p>
                                                                ) : (
                                                                    <div className="space-y-2">
                                                                        {selectedCommitment.participations?.map(p => (
                                                                            <div key={p.id} className="flex items-center justify-between py-2 border-b last:border-0">
                                                                                <div>
                                                                                    <p className="font-medium">{p.investorName}</p>
                                                                                    <p className="text-sm text-gray-500">{p.participationPercentage?.toFixed(2)}% participation</p>
                                                                                </div>
                                                                                <p className="font-semibold">{formatCurrency(p.amount)}</p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Terms */}
                                                            <div className="p-4 bg-white rounded-b-lg">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h5 className="font-medium text-gray-700">Investment Terms (Tranches)</h5>
                                                                    {user?.role !== 'INVESTOR' && (
                                                                        <button
                                                                            onClick={() => { resetTermForm(); setShowTermModal(true); }}
                                                                            className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                                                                        >
                                                                            + Add Term
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {selectedCommitment.terms?.length === 0 ? (
                                                                    <p className="text-gray-500 text-sm">No terms defined</p>
                                                                ) : (
                                                                    <div className="space-y-2">
                                                                        {selectedCommitment.terms?.map(term => (
                                                                            <div key={term.id} className="flex items-center justify-between py-2 border-b last:border-0">
                                                                                <div>
                                                                                    <p className="font-medium">Term #{term.termNumber}</p>
                                                                                    <p className="text-sm text-gray-500">
                                                                                        Due: {term.dueDate || 'Not set'}
                                                                                        {term.milestone && ` - ${term.milestone}`}
                                                                                    </p>
                                                                                </div>
                                                                                <div className="flex items-center space-x-3">
                                                                                    <p className="font-semibold">{formatCurrency(term.amount)}</p>
                                                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${getRoundStatusBadge(term.status)}`}>
                                                                                        {term.status}
                                                                                    </span>
                                                                                    {term.status === 'PENDING' && user?.role !== 'INVESTOR' && (
                                                                                        <button
                                                                                            onClick={() => handleMarkTermPaid(term)}
                                                                                            className="px-2 py-1 text-green-600 hover:bg-green-50 rounded text-sm"
                                                                                        >
                                                                                            Mark Paid
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="bg-gray-50 rounded-lg border p-12 text-center">
                                                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <h3 className="text-lg font-medium text-gray-900 mb-1">Select a Round</h3>
                                                    <p className="text-gray-500">Choose an investment round from the list to view details</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Stage History Tab */}
                {activeTab === 'stage-history' && (
                    <div className="space-y-6">
                        {/* Stage Graph */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">Funding Stage Progression</h2>
                            <div className="relative">
                                {/* Timeline */}
                                <div className="flex justify-between items-center mb-8">
                                    {stageHistory.map((stage, index) => (
                                        <div key={index} className="flex flex-col items-center flex-1">
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${index === stageHistory.length - 1
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-green-600 text-white'
                                                }`}>
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p className="mt-2 text-sm font-medium text-gray-900">{stage.stage}</p>
                                            <p className="text-xs text-gray-500">{stage.date}</p>
                                            {stage.amount && (
                                                <p className="text-sm font-semibold text-green-600 mt-1">{stage.amount}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Connecting Line */}
                                <div className="absolute top-6 left-0 right-0 h-0.5 bg-gray-300" style={{ zIndex: -1 }}></div>
                            </div>
                        </div>

                        {/* Stage Details */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount Raised</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {stageHistory.map((stage, index) => (
                                        <tr key={index}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{stage.stage}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stage.date}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stage.amount || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${index === stageHistory.length - 1
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-green-100 text-green-800'
                                                    }`}>
                                                    {index === stageHistory.length - 1 ? 'Current' : 'Completed'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Directors Tab */}
                {activeTab === 'directors' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Directors & Key Personnel</h2>
                                    <p className="text-sm text-gray-500 mt-1">Manage directors, founders, investors, and board members</p>
                                </div>
                                {user?.role !== 'INVESTOR' && (
                                    <button
                                        onClick={() => {
                                            setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '' });
                                            setShowAddDirectorModal(true);
                                        }}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span>Add Director</span>
                                    </button>
                                )}
                            </div>

                            {directors.length === 0 ? (
                                <div className="text-center py-12">
                                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No directors added</h3>
                                    <p className="mt-1 text-sm text-gray-500">Get started by adding a director or key personnel.</p>
                                </div>
                            ) : (
                                <div className="overflow-hidden border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shareholding</th>
                                                {user?.role !== 'INVESTOR' && (
                                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {directors.map((director) => (
                                                <tr key={director.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center">
                                                                <span className="text-white font-medium">{director.name?.charAt(0) || '?'}</span>
                                                            </div>
                                                            <div className="ml-4">
                                                                <div className="text-sm font-medium text-gray-900">{director.name}</div>
                                                                {director.email && (
                                                                    <div className="text-xs text-gray-500">{director.email}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {director.category ? (
                                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                                                {director.category.replace(/_/g, ' ')}
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{director.role || '-'}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {director.shareholding ? (
                                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                                {director.shareholding}
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    {user?.role !== 'INVESTOR' && (
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                            <button
                                                                onClick={() => handleEditDirector(director)}
                                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                                title="Edit"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setDirectorToDelete(director);
                                                                    setShowDeleteDirectorConfirm(true);
                                                                }}
                                                                className="text-red-600 hover:text-red-900"
                                                                title="Remove"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <CreateStartupModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        loadStartupDetails();
                    }}
                    initialData={startup}
                    isEdit={true}
                />
            )}

            {/* File Preview Modal */}
            <FilePreviewModal
                file={previewFile}
                isOpen={showPreviewModal}
                onClose={() => {
                    setShowPreviewModal(false);
                    setPreviewFile(null);
                }}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Startup?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete "{startup.organizationName || startup.name}"? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                                Cancel
                            </button>
                            <button onClick={handleDeleteStartup} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Director Modal */}
            {showAddDirectorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Director</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={newDirector.name}
                                    onChange={(e) => setNewDirector({ ...newDirector, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Full name"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={newDirector.email}
                                    onChange={(e) => setNewDirector({ ...newDirector, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    value={newDirector.phone}
                                    onChange={(e) => setNewDirector({ ...newDirector, phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+1 234 567 8900"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                                <select
                                    value={newDirector.category}
                                    onChange={(e) => setNewDirector({ ...newDirector, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                >
                                    <option value="">Select Category</option>
                                    <option value="PROMOTORS">Promotors</option>
                                    <option value="INVESTORS">Investors</option>
                                    <option value="DIRECTORS">Directors</option>
                                    <option value="BOARD_OBSERVERS">Board Observers</option>
                                    <option value="INDEPENDENT_DIRECTORS">Independent Directors</option>
                                    <option value="FOUNDER_DIRECTORS">Founder Directors</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role/Designation</label>
                                <input
                                    type="text"
                                    value={newDirector.role}
                                    onChange={(e) => setNewDirector({ ...newDirector, role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., CEO, CTO, CFO, Co-Founder"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Shareholding</label>
                                <input
                                    type="text"
                                    value={newDirector.shareholding}
                                    onChange={(e) => setNewDirector({ ...newDirector, shareholding: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., 25% or 1,000,000 shares"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Aadhar Number <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500 ml-1">(12 digits)</span>
                                </label>
                                <input
                                    type="text"
                                    value={newDirector.aadharNumber}
                                    onChange={(e) => setNewDirector({ ...newDirector, aadharNumber: e.target.value })}
                                    maxLength="12"
                                    pattern="[0-9]{12}"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="XXXX XXXX XXXX"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    PAN Number <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500 ml-1">(10 characters)</span>
                                </label>
                                <input
                                    type="text"
                                    value={newDirector.panNumber}
                                    onChange={(e) => setNewDirector({ ...newDirector, panNumber: e.target.value.toUpperCase() })}
                                    maxLength="10"
                                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                                    placeholder="ABCDE1234F"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddDirectorModal(false);
                                    setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddDirector}
                                disabled={!newDirector.name || !newDirector.category}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                Add Director
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Director Modal */}
            {showEditDirectorModal && editingDirector && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Director</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={newDirector.name}
                                    onChange={(e) => setNewDirector({ ...newDirector, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Full name"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={newDirector.email}
                                    onChange={(e) => setNewDirector({ ...newDirector, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="email@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    value={newDirector.phone}
                                    onChange={(e) => setNewDirector({ ...newDirector, phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+1 234 567 8900"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                                <select
                                    value={newDirector.category}
                                    onChange={(e) => setNewDirector({ ...newDirector, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                >
                                    <option value="">Select Category</option>
                                    <option value="PROMOTORS">Promotors</option>
                                    <option value="INVESTORS">Investors</option>
                                    <option value="DIRECTORS">Directors</option>
                                    <option value="BOARD_OBSERVERS">Board Observers</option>
                                    <option value="INDEPENDENT_DIRECTORS">Independent Directors</option>
                                    <option value="FOUNDER_DIRECTORS">Founder Directors</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role/Designation</label>
                                <input
                                    type="text"
                                    value={newDirector.role}
                                    onChange={(e) => setNewDirector({ ...newDirector, role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., CEO, CTO, CFO, Co-Founder"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Shareholding</label>
                                <input
                                    type="text"
                                    value={newDirector.shareholding}
                                    onChange={(e) => setNewDirector({ ...newDirector, shareholding: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., 25% or 1,000,000 shares"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Aadhar Number <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500 ml-1">(12 digits)</span>
                                </label>
                                <input
                                    type="text"
                                    value={newDirector.aadharNumber}
                                    onChange={(e) => setNewDirector({ ...newDirector, aadharNumber: e.target.value })}
                                    maxLength="12"
                                    pattern="[0-9]{12}"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="XXXX XXXX XXXX"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    PAN Number <span className="text-red-500">*</span>
                                    <span className="text-xs text-gray-500 ml-1">(10 characters)</span>
                                </label>
                                <input
                                    type="text"
                                    value={newDirector.panNumber}
                                    onChange={(e) => setNewDirector({ ...newDirector, panNumber: e.target.value.toUpperCase() })}
                                    maxLength="10"
                                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                                    placeholder="ABCDE1234F"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowEditDirectorModal(false);
                                    setEditingDirector(null);
                                    setNewDirector({ name: '', email: '', phone: '', role: '', category: '', shareholding: '', aadharNumber: '', panNumber: '' });
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateDirector}
                                disabled={!newDirector.name || !newDirector.category}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                Update Director
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Permissions Modal */}
            {showPermissionsModal && selectedFileForPermissions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <PermissionsTab
                            file={selectedFileForPermissions}
                            investors={investors}
                            onBack={() => {
                                setShowPermissionsModal(false);
                                setSelectedFileForPermissions(null);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Investment Round Modal */}
            {showRoundModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">
                                {editingRound ? 'Edit Investment Round' : 'New Investment Round'}
                            </h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Round Name</label>
                                    <select
                                        value={roundForm.roundName}
                                        onChange={(e) => setRoundForm({ ...roundForm, roundName: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    >
                                        <option value="Pre-Seed">Pre-Seed</option>
                                        <option value="Seed">Seed</option>
                                        <option value="Series A">Series A</option>
                                        <option value="Series B">Series B</option>
                                        <option value="Series C">Series C</option>
                                        <option value="Series D">Series D</option>
                                        <option value="Bridge">Bridge</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount</label>
                                    <input
                                        type="number"
                                        value={roundForm.targetAmount}
                                        onChange={(e) => setRoundForm({ ...roundForm, targetAmount: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        placeholder="10000000"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Pre-Money Valuation</label>
                                    <input
                                        type="number"
                                        value={roundForm.preMoneyValuation}
                                        onChange={(e) => setRoundForm({ ...roundForm, preMoneyValuation: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Post-Money Valuation</label>
                                    <input
                                        type="number"
                                        value={roundForm.postMoneyValuation}
                                        onChange={(e) => setRoundForm({ ...roundForm, postMoneyValuation: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Share Price</label>
                                    <input
                                        type="number"
                                        value={roundForm.sharePrice}
                                        onChange={(e) => setRoundForm({ ...roundForm, sharePrice: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Shares</label>
                                    <input
                                        type="number"
                                        value={roundForm.totalShares}
                                        onChange={(e) => setRoundForm({ ...roundForm, totalShares: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Open Date</label>
                                    <input
                                        type="date"
                                        value={roundForm.openDate}
                                        onChange={(e) => setRoundForm({ ...roundForm, openDate: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Close Date</label>
                                    <input
                                        type="date"
                                        value={roundForm.closeDate}
                                        onChange={(e) => setRoundForm({ ...roundForm, closeDate: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={roundForm.notes}
                                    onChange={(e) => setRoundForm({ ...roundForm, notes: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end space-x-3">
                            <button
                                onClick={() => { setShowRoundModal(false); setEditingRound(null); }}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingRound ? handleUpdateRound : handleCreateRound}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                {editingRound ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Commitment Modal */}
            {showCommitmentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">Add Network Commitment</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Investor Network</label>
                                <select
                                    value={commitmentForm.networkId}
                                    onChange={(e) => {
                                        const networkId = e.target.value;
                                        setCommitmentForm({ ...commitmentForm, networkId, selectedMemberIds: [] });
                                        loadNetworkMembers(networkId);
                                    }}
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="">Select Network</option>
                                    {networks.map(n => (
                                        <option key={n.id} value={n.id}>{n.name}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Network's Own Investment */}
                            {commitmentForm.networkId && (
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-medium text-blue-900">Network's Own Investment</h4>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Network Investment Amount</label>
                                        <input
                                            type="number"
                                            value={commitmentForm.networkInvestmentAmount}
                                            onChange={(e) => setCommitmentForm({ ...commitmentForm, networkInvestmentAmount: e.target.value })}
                                            className="w-full px-3 py-2 border rounded-lg"
                                            placeholder="Amount invested by the network itself"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Member Selection */}
                            {commitmentForm.networkId && networkMembers.length > 0 && (
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-medium text-gray-900">Select Participating Members</h4>
                                        <div className="flex space-x-2">
                                            <button
                                                type="button"
                                                onClick={() => setCommitmentForm({ ...commitmentForm, selectedMemberIds: networkMembers.map(m => m.id) })}
                                                className="text-xs text-blue-600 hover:underline"
                                            >
                                                Select All
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                type="button"
                                                onClick={() => setCommitmentForm({ ...commitmentForm, selectedMemberIds: [] })}
                                                className="text-xs text-gray-600 hover:underline"
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto space-y-2">
                                        {networkMembers.map(member => (
                                            <label key={member.id} className="flex items-center p-2 bg-white rounded border hover:bg-gray-50 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={commitmentForm.selectedMemberIds.includes(member.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setCommitmentForm({
                                                                ...commitmentForm,
                                                                selectedMemberIds: [...commitmentForm.selectedMemberIds, member.id]
                                                            });
                                                        } else {
                                                            setCommitmentForm({
                                                                ...commitmentForm,
                                                                selectedMemberIds: commitmentForm.selectedMemberIds.filter(id => id !== member.id)
                                                            });
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                                />
                                                <div className="ml-3 flex-1">
                                                    <p className="text-sm font-medium text-gray-900">{member.name}</p>
                                                    <p className="text-xs text-gray-500">{member.email}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    {commitmentForm.selectedMemberIds.length > 0 && (
                                        <p className="mt-2 text-sm text-gray-600">
                                            {commitmentForm.selectedMemberIds.length} member(s) selected
                                        </p>
                                    )}
                                </div>
                            )}

                            {commitmentForm.networkId && networkMembers.length === 0 && (
                                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                    <p className="text-sm text-yellow-800">This network has no members yet. Only the network itself can invest.</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Committed Amount</label>
                                    <input
                                        type="number"
                                        value={commitmentForm.committedAmount}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, committedAmount: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Investment Type</label>
                                    <select
                                        value={commitmentForm.investmentType}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, investmentType: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    >
                                        <option value="EQUITY">Equity</option>
                                        <option value="CCPS">CCPS</option>
                                        <option value="CCD">CCD</option>
                                        <option value="SAFE">SAFE</option>
                                        <option value="CONVERTIBLE_NOTE">Convertible Note</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Equity %</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={commitmentForm.equityPercentage}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, equityPercentage: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Shares</label>
                                    <input
                                        type="number"
                                        value={commitmentForm.numberOfShares}
                                        onChange={(e) => setCommitmentForm({ ...commitmentForm, numberOfShares: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={commitmentForm.notes}
                                    onChange={(e) => setCommitmentForm({ ...commitmentForm, notes: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end space-x-3">
                            <button
                                onClick={() => setShowCommitmentModal(false)}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateCommitment}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Add Commitment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Participation Modal */}
            {showParticipationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">Add Investor Participation</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Investor</label>
                                <select
                                    value={participationForm.investorId}
                                    onChange={(e) => setParticipationForm({ ...participationForm, investorId: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="">Select Investor</option>
                                    {allInvestors.map(i => (
                                        <option key={i.id} value={i.id}>{i.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                    <input
                                        type="number"
                                        value={participationForm.amount}
                                        onChange={(e) => setParticipationForm({ ...participationForm, amount: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Participation %</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={participationForm.participationPercentage}
                                        onChange={(e) => setParticipationForm({ ...participationForm, participationPercentage: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Shares Allocated</label>
                                <input
                                    type="number"
                                    value={participationForm.sharesAllocated}
                                    onChange={(e) => setParticipationForm({ ...participationForm, sharesAllocated: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={participationForm.notes}
                                    onChange={(e) => setParticipationForm({ ...participationForm, notes: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end space-x-3">
                            <button
                                onClick={() => setShowParticipationModal(false)}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateParticipation}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Add Participation
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Term Modal */}
            {showTermModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="p-6 border-b">
                            <h2 className="text-xl font-bold">Add Investment Term</h2>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                    <input
                                        type="number"
                                        value={termForm.amount}
                                        onChange={(e) => setTermForm({ ...termForm, amount: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage %</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={termForm.percentage}
                                        onChange={(e) => setTermForm({ ...termForm, percentage: e.target.value })}
                                        className="w-full px-3 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                                <input
                                    type="date"
                                    value={termForm.dueDate}
                                    onChange={(e) => setTermForm({ ...termForm, dueDate: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Milestone</label>
                                <input
                                    type="text"
                                    value={termForm.milestone}
                                    onChange={(e) => setTermForm({ ...termForm, milestone: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    placeholder="e.g., Product launch, Revenue target"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    value={termForm.notes}
                                    onChange={(e) => setTermForm({ ...termForm, notes: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t flex justify-end space-x-3">
                            <button
                                onClick={() => setShowTermModal(false)}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateTerm}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Add Term
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Dialog */}
            <CustomDialog
                isOpen={showErrorDialog}
                onClose={() => setShowErrorDialog(false)}
                title="Update Failed"
                message={errorMessage}
                type="error"
                confirmText="OK"
            />

            {/* Success Dialog */}
            <CustomDialog
                isOpen={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                title="Success"
                message={successMessage}
                type="success"
                confirmText="OK"
            />

            {/* Delete Director Confirmation */}
            <ConfirmDialog
                isOpen={showDeleteDirectorConfirm}
                onClose={() => {
                    setShowDeleteDirectorConfirm(false);
                    setDirectorToDelete(null);
                }}
                onConfirm={() => {
                    if (directorToDelete) {
                        handleDeleteDirector(directorToDelete.id);
                        setShowDeleteDirectorConfirm(false);
                        setDirectorToDelete(null);
                    }
                }}
                title="Delete Director"
                message={`Are you sure you want to delete ${directorToDelete?.name}? This action cannot be undone.`}
            />
            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
                onConfirm={confirm.onConfirm}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
            />
        </>
    );
};

export default StartupDetail;
